import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "382",
  "slug": "seven-summits-highest-mountain-every-continent",
  "title": "The Seven Summits: The Highest Mountain on Every Continent",
  "excerpt": "Everest is the tallest mountain on Earth, but every continent has its own high point. Here are the Seven Summits, why climbers chase them, and the one peak that still starts arguments.",
  "date": "2026-06-09",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1585773622274-3be290c6f8f8?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzbm93eSUyMG1vdW50YWluJTIwcGVha3xlbnwxfDB8fHwxNzgwOTkyODEwfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The Seven Summits are the highest peaks on each continent: Everest (Asia), Aconcagua (South America), Denali (North America), Kilimanjaro (Africa), Elbrus (Europe), Vinson (Antarctica), and either Carstensz Pyramid or Mount Kosciuszko depending on how you define Australia and Oceania.",
  "content": [
    {
      "type": "paragraph",
      "text": "Ask someone to name the tallest mountain in the world and most people will land on Everest. But there is a more interesting question hiding behind that one: what is the highest point on each of the seven continents? Put those seven peaks together and you get the Seven Summits, one of the most famous challenges in all of mountaineering and a surprisingly good way to learn the shape of the planet."
    },
    {
      "type": "heading",
      "text": "What Are the Seven Summits?"
    },
    {
      "type": "paragraph",
      "text": "The Seven Summits are exactly what the name suggests: the highest mountain on each of Earth’s seven continents. The idea was popularized by American businessman and climber Richard Bass, who in 1985 became the first person to stand on all seven. Reaching them means traveling to wildly different environments, from the equatorial glaciers of Africa to the frozen interior of Antarctica, which is part of why the challenge captures the imagination far beyond the climbing community."
    },
    {
      "type": "paragraph",
      "text": "What makes the list a geography lesson rather than just a checklist is how spread out the peaks are. They sit on different tectonic stories, different climate bands, and different hemispheres. Learning them teaches you continents, mountain ranges, and the forces that build high ground all at once."
    },
    {
      "type": "heading",
      "text": "The Seven Peaks, One by One"
    },
    {
      "type": "paragraph",
      "text": "Here are the high points by continent, from the giant to the gentle:"
    },
    {
      "type": "list",
      "items": [
        "Asia — Mount Everest, on the Nepal–China border, at about 8,849 metres. The roof of the world and the highest point on land.",
        "South America — Aconcagua, in the Argentine Andes, at roughly 6,961 metres. The tallest mountain outside Asia.",
        "North America — Denali, in Alaska, at about 6,190 metres. Its rise from the surrounding lowland is one of the largest of any mountain on Earth.",
        "Africa — Kilimanjaro, in Tanzania, at around 5,895 metres. A free-standing volcano with glaciers sitting almost on the equator.",
        "Europe — Mount Elbrus, in the Russian Caucasus, at about 5,642 metres. A dormant volcano that edges out the Alps.",
        "Antarctica — Mount Vinson, at roughly 4,892 metres. Deep in the continent, hundreds of kilometres from anything.",
        "Australia/Oceania — either Carstensz Pyramid (Puncak Jaya) at about 4,884 metres, or Mount Kosciuszko at 2,228 metres, depending on the version of the list."
      ]
    },
    {
      "type": "heading",
      "text": "The Australia vs. Oceania Debate"
    },
    {
      "type": "paragraph",
      "text": "That last entry is where mountaineers split into two camps. Richard Bass’s original list used Mount Kosciuszko, the highest point on the Australian mainland, an easy walk-up that barely tops 2,200 metres. The Italian climber Reinhold Messner argued that the relevant landmass is not the country of Australia but the wider continent of Oceania, which makes Carstensz Pyramid in Indonesia the true high point, a far more technical climb through jungle and rock."
    },
    {
      "type": "paragraph",
      "text": "Neither list is wrong; they answer slightly different questions. The Bass list asks for the highest point of each continent in the classic seven-continent model. The Messner list leans on a stricter definition of continental landmass. Today most serious mountaineers chase the Messner version precisely because Carstensz is the harder, more meaningful objective."
    },
    {
      "type": "heading",
      "text": "Why the Seven Summits Became a Bucket List"
    },
    {
      "type": "paragraph",
      "text": "Part of the appeal is symbolic: standing on the top of every continent feels like touching the whole planet. But the challenge is also genuinely diverse. Kilimanjaro is mostly a long hike at altitude. Everest and Denali demand serious cold-weather mountaineering. Vinson requires an expedition to one of the most remote places humans ever go. No single skill gets you all seven, which is why completing them carries real weight."
    },
    {
      "type": "paragraph",
      "text": "It is also a lesson in logistics and geography. To finish the Seven Summits you have to understand seasons in both hemispheres, the politics of access in different countries, and the wildly different weather windows each peak allows."
    },
    {
      "type": "heading",
      "text": "What the Map Teaches You"
    },
    {
      "type": "paragraph",
      "text": "Even if you never rope up, the Seven Summits are a fantastic mental map. Memorize them and you have anchored yourself to all seven continents, the major mountain belts, and the difference between a volcanic high point like Kilimanjaro and a folded-range giant like Aconcagua. It is the kind of framework that makes the rest of world geography easier to hang together."
    },
    {
      "type": "heading",
      "text": "Spot Them From Above"
    },
    {
      "type": "paragraph",
      "text": "From orbit, these peaks announce themselves in different ways. Kilimanjaro is a lone shadowed cone rising from the East African plains. The Andes around Aconcagua form a sharp brown spine. Antarctica’s Vinson hides in a sea of white. Learning to recognize mountain ranges from satellite view, by their texture, their snow lines, and the rivers that drain them, is one of the most satisfying geography skills you can build."
    }
  ]
};
